import React from "react";
import { Link } from "react-router-dom";
import { Hammer, Phone } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { localized } from "@/theme/tenantTheme";
import { tradeLabel } from "@/lib/format";
import { StarRating } from "@/components/StarRating";
import { resolveImage } from "@/lib/media";
import { FavoriteButton } from "@/components/FavoriteButton";

export function WorkerCard({ worker }) {
  const { lang, t } = useApp();
  return (
    <Link
      to={`/w/${worker.id}`}
      data-testid={`worker-card-${worker.id}`}
      className="relative flex gap-4 rounded-2xl border border-border bg-card p-5 pr-12 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md"
    >
      <FavoriteButton targetType="worker" targetId={worker.id} className="absolute right-3 top-3" />
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-muted">
        {worker.photo_url ? (
          <img src={resolveImage(worker.photo_url)} alt={worker.name} width={64} height={64} loading="lazy" decoding="async" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <Hammer size={22} />
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1">
        <h3 className="font-heading text-base font-semibold">{worker.name}</h3>
        <p className="text-sm text-[hsl(var(--primary))]">{tradeLabel(lang, worker)}</p>
        <p className="text-xs text-muted-foreground">
          {t("directory.experience", { years: worker.experience_years, count: worker.experience_years })}
        </p>
        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{localized(lang, worker, "about")}</p>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <StarRating value={worker.rating_avg} count={worker.rating_count} />
          <span
            className={`text-xs font-medium ${worker.is_available ? "text-[hsl(var(--primary))]" : "text-muted-foreground"}`}
          >
            {worker.is_available ? t("directory.available") : t("directory.busy")}
          </span>
          {worker.phone && (
            <span
              role="link"
              tabIndex={0}
              data-testid={`worker-call-${worker.id}`}
              className="flex items-center gap-1 text-xs font-medium text-foreground underline decoration-dotted"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = `tel:${worker.phone}`;
              }}
            >
              <Phone size={12} /> {t("common.call")}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
